import React from "react";
import Plot from "react-plotly.js";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import "./PlotlyGraph.scss"; 
import { Divider } from "@mui/material";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import Graph from "../Model/Graph";

const PlotlyGraph = ({ plotData, rearrangeReferences }) => {

  console.log("PlotlyGraph plotData", plotData);
  
  const exportToPDF = (id, title) => {
    const element = document.getElementById(id);
  
    // Hide controls by adding the no-export class
    const controls = element.querySelectorAll(".export-btn, .expand-btn");
    controls.forEach(btn => btn.classList.add("no-export"));
  
    html2canvas(element, { scale: 2 }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
  
      const imgWidth = 180;
      const pageWidth = pdf.internal.pageSize.getWidth();
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const marginTop = 20;
      const marginLeft = (pageWidth - imgWidth) / 2;
  
      pdf.addImage(imgData, "PNG", marginLeft, marginTop, imgWidth, imgHeight);
      pdf.save(`${title || "chart"}.pdf`);
  
      // Restore buttons
      controls.forEach(btn => btn.classList.remove("no-export"));
    });
  };
  
  if (!plotData || !Array.isArray(plotData) || plotData.length === 0) {
    return null;
  }

  return (
    <div style={{ gap: "5rem" }} className="grid grid-cols-1 bg-white border p-8 rounded">
      {plotData.map((plot, idx) => {
        const chartId = `plot-container-${idx}`;

        const chartData =
          plot.data && plot.data[0]?.type === "indicator"
            ? [
                {
                  type: "indicator",
                  mode: "number+gauge",
                  value: plot.data[0].y ? plot.data[0].y[0] ?? 0 : 0,
                  title: { text: plot.data[0].name || "" },
                  gauge: {
                    // axis: { range: [0, 100] },
                    axis: { 
                      range: [
                        Math.min(0, plot.data[0].y[0] - 10),  // extend below if negative
                        Math.max(100, plot.data[0].y[0] + 10) // extend above value if >100
                      ] 
                    },
                    bar: { color: "#bb1ccc" },
                  },
                },
              ]
            : plot.data;

        return (
          <div key={idx} id={chartId} className="plot-card p-1">
            <div className="mb-4">
              <div
                style={{ justifyContent: "space-between", alignItems: "center" }}
                className="mb-3 flex"
              >
                <span style={{ fontSize: "24px" }} className="text-lg font-semibold text-gray-800">
                  {plot.title}
                </span>
                <button
                  onClick={() => exportToPDF(chartId, plot.title)}
                  style={{border:"none"}}
                  className="bg-purple-500 p-2 text-white rounded"
                >
                  Export in PDF
                </button>
              </div>
              <div style={{ fontSize: "20px" }} className="text-gray-600 leading-relaxed">
                <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                  {rearrangeReferences(plot.context || "No summary available")}
                </ReactMarkdown>
              </div>
            </div>

            <div className="rounded-lg p-2 border bg-gray-100 relative">
              <Graph chartData={chartData} plot={plot} />
              <Plot
                data={chartData}
                layout={{
                  ...plot.layout,
                  autosize: true,
                  responsive: true,
                  showlegend: true,
                  
                  legend: {
                    orientation: "h",
                    x: 0.5,
                    xanchor: "center",
                    y: -0.7,
                    yanchor: "top",
                  },
                  plot_bgcolor: "rgba(0,0,0,0)",
                  paper_bgcolor: "rgba(0,0,0,0)",
                  font: { family: "Arial, sans-serif" },
                  title: { font: { size: 16, family: "Arial, sans-serif" } },
                  // color: ["#0000a0", "#0000a0", "#32a852", "#6d32a8", "#009966", "#cc0000"],
                  colorway: ["#bb1ccc", "#a89e32", "#32a852", "#6d32a8", "#009966", "#cc0000"],
                  xaxis: {
                    title: { 
                      text: plot.layout?.xaxis?.title || "X Axis",   // fallback if not set

                      font: { size: 12, family: "Arial, sans-serif" } },
                    tickangle: -45,
                    tickfont: { size: 10 },
                    automargin: true,
                  },
                  yaxis: {
                    title: { 
                      text: plot.layout?.yaxis?.title || "Y Axis",   // fallback if not set
                      font: { size: 12, family: "Arial, sans-serif" } 
                    },
                    tickfont: { size: 10 },
                  },
                }}
                useResizeHandler={true}
                style={{ width: "100%", minHeight: "450px" }}
                config={{
                  displaylogo: false,
                  modeBarButtonsToRemove: [
                    "lasso2d",
                    "select2d",
                    "hoverClosestCartesian",
                    "hoverCompareCartesian",
                    "toggleSpikelines",
                  ],
                  responsive: true,
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PlotlyGraph;
